import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { isPublicSite } from '../../utils/is-public-site';

/** How often a long-lived tab re-checks for a newly deployed version. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Toast shown when a new app version has been deployed. Also polls for new
 * service-worker versions hourly and whenever the tab regains visibility —
 * long-lived tabs and installed PWA windows never re-fetch sw.js on their
 * own, which left them running a stale cached build indefinitely.
 *
 * ⚠️ **The public marketing site takes the update instead of being asked
 * about it.** The service worker precaches the build, so a returning visitor
 * is served the one they saw last; in `prompt` mode the only way out is this
 * toast. That is right for the producer app — reloading a planner mid-edit
 * loses their work, which is why the registration is `prompt` and not
 * `autoUpdate` — and wrong for a marketing page, where the visitor has no
 * stake in a version, no idea what the toast means, and every reason to
 * expect the site the office just published. So on the public hostnames the
 * new build is applied and the page reloads once, silently. There is no loop:
 * after the reload the new worker is the active one and `needRefresh` is
 * false.
 */
export function PwaUpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const publicSite = isPublicSite();

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (reg) setRegistration(reg);
    },
  });

  // Polling lives in an effect so the interval and visibility listener are
  // registered once and removed on unmount.
  useEffect(() => {
    if (!registration) return;
    const checkForUpdate = () => {
      registration.update().catch(() => {
        /* offline / transient network error — retry on the next tick */
      });
    };
    const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [registration]);

  useEffect(() => {
    if (needRefresh && publicSite) {
      // Activates the waiting worker and reloads onto the new build.
      updateServiceWorker(true);
    }
  }, [needRefresh, publicSite, updateServiceWorker]);

  if (!needRefresh || publicSite) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#1e3a5f',
        color: 'white',
        padding: '0.75rem 1.25rem',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        zIndex: 9999,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: '0.875rem' }}>New version available</span>
      <button
        onClick={() => updateServiceWorker(true)}
        style={{
          background: 'white',
          color: '#1e3a5f',
          border: 'none',
          padding: '0.35rem 0.75rem',
          borderRadius: '8px',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: '0.8rem',
        }}
      >
        Update
      </button>
    </div>
  );
}
